import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import BrandLogo from '../components/shared/BrandLogo';
import { BRAND_HERO_GRADIENT } from '../components/shared/SectionTitle';

const LAST_UPDATED = 'February 17, 2026';
const EFFECTIVE_DATE = 'February 17, 2026';

const SECTIONS = [
  { id: 'what-are-cookies', title: '1. What Are Cookies?' },
  { id: 'types-of-cookies', title: '2. Types of Cookies We Use' },
  { id: 'third-party-cookies', title: '3. Third-Party Cookies' },
  { id: 'cookie-list', title: '4. Detailed Cookie List' },
  { id: 'managing-cookies', title: '5. Managing Your Cookie Preferences' },
  { id: 'do-not-track', title: '6. Do Not Track' },
  { id: 'local-storage', title: '7. Local Storage and Session Storage' },
  { id: 'changes', title: '8. Changes to This Policy' },
  { id: 'contact', title: '9. Contact Us' },
];

const COOKIE_CATEGORIES = [
  {
    id: 'essential',
    name: 'Essential Cookies',
    required: true,
    color: 'green',
    description:
      'These cookies are strictly necessary for OmniConnX to function. They enable core functionality such as authentication, security, and account management. The Service cannot be provided without these cookies.',
    examples: [
      { name: 'omni_session', purpose: 'Maintains your authenticated session across page loads', duration: 'Session', provider: 'OmniConnX' },
      { name: 'csrf_token', purpose: 'Prevents Cross-Site Request Forgery (CSRF) attacks', duration: 'Session', provider: 'OmniConnX' },
      { name: 'omni_auth', purpose: 'Stores a secure authentication token (httpOnly, Secure)', duration: '30 days', provider: 'OmniConnX' },
      { name: '__stripe_sid', purpose: 'Stripe session for fraud prevention on payment flows', duration: '30 minutes', provider: 'Stripe' },
      { name: '__stripe_mid', purpose: 'Stripe machine identifier for fraud prevention', duration: '1 year', provider: 'Stripe' },
    ],
  },
  {
    id: 'analytics',
    name: 'Analytics Cookies',
    required: false,
    color: 'blue',
    description:
      'These cookies help us understand how users interact with OmniConnX, which pages are most popular, and where errors occur. All data is anonymized and aggregated — we cannot identify you individually from analytics data.',
    examples: [
      { name: '_ga', purpose: 'Google Analytics: distinguishes unique users (anonymized IP)', duration: '2 years', provider: 'Google Analytics' },
      { name: '_ga_*', purpose: 'Google Analytics 4: stores and counts page views', duration: '2 years', provider: 'Google Analytics' },
      { name: 'omni_analytics_session', purpose: 'Tracks feature usage within a session (no PII)', duration: 'Session', provider: 'OmniConnX' },
      { name: 'sentry-sc', purpose: 'Sentry error tracking session identifier (anonymized)', duration: 'Session', provider: 'Sentry' },
    ],
  },
  {
    id: 'functional',
    name: 'Functional Cookies',
    required: false,
    color: 'purple',
    description:
      'These cookies remember your preferences and settings to provide a personalized experience. They are not essential for the core Service but significantly improve usability.',
    examples: [
      { name: 'omni_theme', purpose: 'Remembers your light/dark mode preference', duration: '1 year', provider: 'OmniConnX' },
      { name: 'omni_sidebar_state', purpose: 'Remembers sidebar collapsed/expanded state', duration: '6 months', provider: 'OmniConnX' },
      { name: 'omni_locale', purpose: 'Stores your language and regional format preferences', duration: '1 year', provider: 'OmniConnX' },
      { name: 'omni_dashboard_layout', purpose: 'Remembers your dashboard widget arrangement', duration: '1 year', provider: 'OmniConnX' },
    ],
  },
  {
    id: 'marketing',
    name: 'Marketing Cookies',
    required: false,
    color: 'orange',
    description:
      'OmniConnX does not use marketing or advertising cookies on the main application. On our public marketing website (omniconnx.com), we may use limited measurement cookies to understand which marketing channels drive sign-ups. These are never used inside the authenticated app.',
    examples: [
      { name: 'omni_referral', purpose: 'Tracks referral source for signup attribution (anonymized)', duration: '90 days', provider: 'OmniConnX' },
      { name: 'omni_utm_*', purpose: 'Stores UTM campaign parameters to measure marketing effectiveness', duration: '30 days', provider: 'OmniConnX' },
    ],
  },
];

const COLOR_MAP = {
  green: { badge: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400', border: 'border-green-200 dark:border-green-800', header: 'bg-green-50 dark:bg-green-900/20' },
  blue: { badge: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800', header: 'bg-blue-50 dark:bg-blue-900/20' },
  purple: { badge: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400', border: 'border-purple-200 dark:border-purple-800', header: 'bg-purple-50 dark:bg-purple-900/20' },
  orange: { badge: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400', border: 'border-orange-200 dark:border-orange-800', header: 'bg-orange-50 dark:bg-orange-900/20' },
};

const CookiePolicyPage = () => {
  const [activeSection, setActiveSection] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
      const sectionEls = SECTIONS.map(s => document.getElementById(s.id));
      for (let i = sectionEls.length - 1; i >= 0; i--) {
        const el = sectionEls[i];
        if (el && el.getBoundingClientRect().top <= 120) {
          setActiveSection(SECTIONS[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-white dark:bg-dark-950">
      {/* Header */}
      <header className="border-b border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-900 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <BrandLogo size="md" />
          </Link>
          <nav className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Link to="/" className="hover:text-gray-900 dark:hover:text-white transition-colors">Home</Link>
            <ChevronRightIcon className="w-3 h-3" />
            <span className="text-gray-900 dark:text-white font-medium">Cookie Policy</span>
          </nav>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="lg:grid lg:grid-cols-4 lg:gap-12">
          {/* Sidebar TOC */}
          <aside className="hidden lg:block lg:col-span-1">
            <div className="sticky top-28">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                Table of Contents
              </p>
              <nav className="space-y-1">
                {SECTIONS.map(section => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className={`block text-sm py-1.5 px-3 rounded-lg transition-colors ${
                      activeSection === section.id
                        ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 font-medium'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-dark-800'
                    }`}
                  >
                    {section.title}
                  </a>
                ))}
              </nav>

              {/* Cookie Category Legend */}
              <div className="mt-8 space-y-2">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  Cookie Categories
                </p>
                {COOKIE_CATEGORIES.map(cat => (
                  <div key={cat.id} className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded-lg ${COLOR_MAP[cat.color].badge}`}>
                    <span>{cat.name}</span>
                    {cat.required && (
                      <span className="ml-auto font-semibold">Required</span>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800">
                <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-1">Last Updated</p>
                <p className="text-xs text-blue-600 dark:text-blue-300">{LAST_UPDATED}</p>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-3">
            <div className="mb-10">
              <div className="h-1.5 w-24 rounded-full mb-5" style={{ background: BRAND_HERO_GRADIENT }} aria-hidden />
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Cookie Policy</h1>
              <p className="text-gray-600 dark:text-gray-400">
                <strong>Effective Date:</strong> {EFFECTIVE_DATE}
              </p>
              <p className="mt-4 text-gray-700 dark:text-gray-300 leading-relaxed">
                This Cookie Policy explains how OmniConnX, Inc. ("OmniConnX," "we," "us," or "our") uses cookies and
                similar tracking technologies on our website and platform. It should be read alongside our{' '}
                <Link to="/privacy" className="text-violet-600 dark:text-violet-400 hover:underline">Privacy Policy</Link>.
              </p>

              {/* Cookie Consent Banner Note */}
              <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-xl">
                <p className="text-sm text-green-800 dark:text-green-300">
                  <strong>Your Choices Matter:</strong> When you first visit OmniConnX, we show a cookie consent banner.
                  You can choose to accept or decline non-essential cookies. You can update your preferences at any time in
                  Settings → Privacy → Cookie Preferences.
                </p>
              </div>
            </div>

            <div className="space-y-12 text-gray-700 dark:text-gray-300 leading-relaxed">

              {/* Section 1 */}
              <section id="what-are-cookies">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">1. What Are Cookies?</h2>
                <p className="mb-4">
                  Cookies are small text files placed on your device (computer, tablet, or mobile) by websites you visit.
                  They are widely used to make websites work more efficiently, remember your preferences, and provide
                  information to website operators about how their site is used.
                </p>
                <p className="mb-4">
                  Cookies are not programs that can harm your computer. They cannot access other files on your device or
                  execute code. They simply store key-value pairs of text information.
                </p>
                <p>
                  Cookies have two main attributes:
                </p>
                <ul className="list-disc list-outside ml-5 space-y-2 mt-3">
                  <li>
                    <strong>Session cookies:</strong> Temporary cookies that expire when you close your browser. They help
                    navigate the Service within a single browsing session.
                  </li>
                  <li>
                    <strong>Persistent cookies:</strong> Cookies that remain on your device for a set period (or until you
                    delete them). They help OmniConnX recognize you on return visits.
                  </li>
                  <li>
                    <strong>First-party cookies:</strong> Set directly by OmniConnX on our domain.
                  </li>
                  <li>
                    <strong>Third-party cookies:</strong> Set by external services we use (e.g., Stripe for payments, Google
                    Analytics for usage insights).
                  </li>
                </ul>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 2 */}
              <section id="types-of-cookies">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">2. Types of Cookies We Use</h2>
                <p className="mb-8">
                  We use four categories of cookies on OmniConnX. Below is a summary of each category, along with
                  whether they can be disabled and their purpose.
                </p>

                <div className="space-y-6">
                  {COOKIE_CATEGORIES.map(cat => {
                    const colors = COLOR_MAP[cat.color];
                    return (
                      <div key={cat.id} className={`rounded-2xl border ${colors.border} overflow-hidden`}>
                        <div className={`p-5 ${colors.header} flex items-center justify-between`}>
                          <div className="flex items-center gap-3">
                            <span className={`text-sm font-bold px-3 py-1 rounded-full ${colors.badge}`}>
                              {cat.name}
                            </span>
                          </div>
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                            cat.required
                              ? 'bg-gray-200 dark:bg-dark-600 text-gray-700 dark:text-gray-300'
                              : 'bg-white dark:bg-dark-700 text-gray-600 dark:text-gray-400'
                          }`}>
                            {cat.required ? 'Always Active' : 'Optional — Your Choice'}
                          </span>
                        </div>
                        <div className="p-5 bg-white dark:bg-dark-900">
                          <p className="text-sm leading-relaxed">{cat.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 3 */}
              <section id="third-party-cookies">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">3. Third-Party Cookies</h2>
                <p className="mb-6">
                  Some cookies on OmniConnX are set by third-party services. These services have their own privacy
                  policies governing their use of cookies. We have no control over third-party cookies beyond whether
                  to include the third-party service.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200 dark:border-dark-600 rounded-xl overflow-hidden">
                    <thead className="bg-gray-50 dark:bg-dark-800">
                      <tr>
                        <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-300">Third Party</th>
                        <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-300">Purpose</th>
                        <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-300">Category</th>
                        <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-300">Privacy Policy</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-dark-600">
                      {[
                        {
                          provider: 'Stripe, Inc.',
                          purpose: 'Payment processing and fraud prevention',
                          category: 'Essential',
                          privacyUrl: 'https://stripe.com/privacy',
                        },
                        {
                          provider: 'Google Analytics',
                          purpose: 'Anonymized usage analytics to improve the product',
                          category: 'Analytics',
                          privacyUrl: 'https://policies.google.com/privacy',
                        },
                        {
                          provider: 'Sentry, Inc.',
                          purpose: 'Error monitoring and performance tracking (anonymized)',
                          category: 'Analytics',
                          privacyUrl: 'https://sentry.io/privacy/',
                        },
                      ].map(({ provider, purpose, category, privacyUrl }) => (
                        <tr key={provider} className="bg-white dark:bg-dark-900">
                          <td className="p-3 font-medium text-gray-900 dark:text-white">{provider}</td>
                          <td className="p-3">{purpose}</td>
                          <td className="p-3">
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                              category === 'Essential'
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                            }`}>
                              {category}
                            </span>
                          </td>
                          <td className="p-3">
                            <a
                              href={privacyUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-violet-600 dark:text-violet-400 hover:underline text-xs"
                            >
                              View policy →
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 4 */}
              <section id="cookie-list">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">4. Detailed Cookie List</h2>
                <p className="mb-8">
                  The table below lists the specific cookies OmniConnX uses or may use, their purpose, duration, and category.
                  This list may be updated as we add or remove features.
                </p>

                {COOKIE_CATEGORIES.map(cat => {
                  const colors = COLOR_MAP[cat.color];
                  return (
                    <div key={cat.id} className="mb-10">
                      <h3 className={`inline-flex items-center gap-2 text-sm font-bold mb-4 px-3 py-1.5 rounded-full ${colors.badge}`}>
                        {cat.name}
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm border border-gray-200 dark:border-dark-600 rounded-xl overflow-hidden">
                          <thead className="bg-gray-50 dark:bg-dark-800">
                            <tr>
                              <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-300">Cookie Name</th>
                              <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-300">Purpose</th>
                              <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-300">Duration</th>
                              <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-300">Provider</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-dark-600">
                            {cat.examples.map(cookie => (
                              <tr key={cookie.name} className="bg-white dark:bg-dark-900">
                                <td className="p-3 font-mono text-xs text-gray-900 dark:text-white">{cookie.name}</td>
                                <td className="p-3 text-gray-600 dark:text-gray-400">{cookie.purpose}</td>
                                <td className="p-3 text-gray-500 dark:text-gray-500 whitespace-nowrap">{cookie.duration}</td>
                                <td className="p-3 text-gray-500 dark:text-gray-500">{cookie.provider}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 5 */}
              <section id="managing-cookies">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">5. Managing Your Cookie Preferences</h2>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">5.1 OmniConnX Cookie Settings</h3>
                <p className="mb-6">
                  You can update your cookie preferences at any time through your account settings. Go to
                  <strong className="text-gray-900 dark:text-white"> Settings → Privacy → Cookie Preferences </strong>
                  to toggle analytics and functional cookies on or off. Essential cookies cannot be disabled as they are
                  required for the Service to function.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">5.2 Browser Controls</h3>
                <p className="mb-4">
                  You can control cookies through your browser settings. Note that disabling cookies may affect the
                  functionality of the Service. Here is how to manage cookies in common browsers:
                </p>
                <div className="grid sm:grid-cols-2 gap-3 mb-6">
                  {[
                    { browser: 'Google Chrome', path: 'Settings → Privacy and Security → Cookies' },
                    { browser: 'Mozilla Firefox', path: 'Settings → Privacy & Security → Cookies and Site Data' },
                    { browser: 'Apple Safari', path: 'Preferences → Privacy → Manage Website Data' },
                    { browser: 'Microsoft Edge', path: 'Settings → Cookies and Site Permissions → Manage and Delete' },
                  ].map(({ browser, path }) => (
                    <div key={browser} className="p-3 rounded-xl border border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-800">
                      <p className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{browser}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{path}</p>
                    </div>
                  ))}
                </div>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">5.3 Opt-Out of Analytics</h3>
                <p className="mb-4">
                  To opt out of Google Analytics tracking across all websites, you can install the{' '}
                  <a
                    href="https://tools.google.com/dlpage/gaoptout"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-violet-600 dark:text-violet-400 hover:underline"
                  >
                    Google Analytics Opt-out Browser Add-on
                  </a>.
                  Alternatively, disable analytics cookies in your OmniConnX Cookie Preferences.
                </p>
                <p>
                  <strong>Note:</strong> Opting out of analytics does not log you out of OmniConnX or affect access to
                  any features. It only prevents anonymous usage data collection.
                </p>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 6 */}
              <section id="do-not-track">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">6. Do Not Track</h2>
                <p className="mb-4">
                  Some browsers transmit "Do Not Track" (DNT) signals to websites. There is currently no universally accepted
                  standard for how websites should respond to DNT signals.
                </p>
                <p className="mb-4">
                  OmniConnX honors DNT signals as follows:
                </p>
                <ul className="list-disc list-outside ml-5 space-y-2 mb-4">
                  <li>When DNT is enabled, we disable all non-essential analytics cookies by default.</li>
                  <li>Essential cookies (authentication, security) are still set regardless of DNT, as they are required for the Service.</li>
                  <li>You can explicitly override DNT defaults in your Cookie Preferences settings.</li>
                </ul>
                <p>
                  We support the Global Privacy Control (GPC) signal as a valid opt-out of analytics tracking under
                  applicable laws including CCPA.
                </p>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 7 */}
              <section id="local-storage">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">7. Local Storage and Session Storage</h2>
                <p className="mb-4">
                  In addition to cookies, OmniConnX uses your browser's <strong>localStorage</strong> and{' '}
                  <strong>sessionStorage</strong> to store certain preference data client-side. Unlike cookies, these are
                  not transmitted to our servers with every request.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200 dark:border-dark-600 rounded-xl overflow-hidden">
                    <thead className="bg-gray-50 dark:bg-dark-800">
                      <tr>
                        <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-300">Key</th>
                        <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-300">Storage Type</th>
                        <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-300">Purpose</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-dark-600">
                      {[
                        { key: 'omni_theme', type: 'localStorage', purpose: 'Stores dark/light mode preference' },
                        { key: 'omni_sidebar_collapsed', type: 'localStorage', purpose: 'Stores sidebar expand/collapse state' },
                        { key: 'omni_dashboard_filters', type: 'sessionStorage', purpose: 'Stores active dashboard filter state (cleared on tab close)' },
                        { key: 'omni_cookie_consent', type: 'localStorage', purpose: 'Stores your cookie consent choices' },
                        { key: 'omni_onboarding_complete', type: 'localStorage', purpose: 'Tracks whether the onboarding wizard has been completed' },
                      ].map(({ key, type, purpose }) => (
                        <tr key={key} className="bg-white dark:bg-dark-900">
                          <td className="p-3 font-mono text-xs text-gray-900 dark:text-white">{key}</td>
                          <td className="p-3">
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                              type === 'localStorage'
                                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                                : 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400'
                            }`}>
                              {type}
                            </span>
                          </td>
                          <td className="p-3 text-gray-600 dark:text-gray-400">{purpose}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-4 text-sm">
                  You can clear localStorage and sessionStorage data through your browser's developer tools or by deleting
                  your OmniConnX account. Note that clearing these will reset your preferences (e.g., dark mode will revert
                  to system default).
                </p>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 8 */}
              <section id="changes">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">8. Changes to This Policy</h2>
                <p className="mb-4">
                  We may update this Cookie Policy to reflect changes in the technologies we use or applicable regulations.
                  When we make material changes, we will:
                </p>
                <ul className="list-disc list-outside ml-5 space-y-2 mb-4">
                  <li>Update the "Last Updated" date at the top of this page.</li>
                  <li>Notify registered users via email if the changes significantly affect your rights.</li>
                  <li>Re-display the cookie consent banner if we add new cookie categories.</li>
                </ul>
                <p>
                  We encourage you to review this policy periodically. Continued use of the Service after changes take
                  effect constitutes acceptance of the updated Cookie Policy.
                </p>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 9 */}
              <section id="contact">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">9. Contact Us</h2>
                <p className="mb-6">
                  If you have questions about our use of cookies or this Cookie Policy, please contact us:
                </p>
                <div className="bg-gray-50 dark:bg-dark-800 rounded-2xl p-6 border border-gray-200 dark:border-dark-700">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white mb-2">Privacy Team</p>
                      <p className="text-sm">
                        <a href="mailto:info@omniconnx.com" className="text-violet-600 dark:text-violet-400 hover:underline">
                          info@omniconnx.com
                        </a>
                      </p>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white mb-2">Cookie Settings</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Manage preferences in-app: <br />
                        Settings → Privacy → Cookie Preferences
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Footer Links */}
              <div className="pt-4 pb-12 flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
                <span>Related:</span>
                <Link to="/privacy" className="text-violet-600 dark:text-violet-400 hover:underline">Privacy Policy</Link>
                <Link to="/terms" className="text-violet-600 dark:text-violet-400 hover:underline">Terms of Service</Link>
                <Link to="/contact" className="text-violet-600 dark:text-violet-400 hover:underline">Contact Us</Link>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Back to Top */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 text-white p-3 rounded-full shadow-lg transition-all z-50"
          aria-label="Back to top"
        >
          <ChevronUpIcon className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export default CookiePolicyPage;
